import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Music, Users, MapPin, ArrowRight, Laptop, Radio, Headphones } from 'lucide-react';

const HomePage: React.FC = () => {
  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.pexels.com/photos/2747448/pexels-photo-2747448.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" 
            alt="Musicians playing together" 
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-gray-900 via-gray-900/70 to-gray-900"></div>
        </div>
        
        <div className="container mx-auto px-4 z-10 text-center">
          <motion.h1 
            className="text-4xl md:text-6xl font-bold mb-6 text-white"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Find Your <span className="text-purple-500">Perfect Jam Session</span>
          </motion.h1>
          
          <motion.p 
            className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Connect with local musicians based on your instrument, skill level, and location. 
            Start making music together today.
          </motion.p>
          
          <motion.div
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <Link 
              to="/register" 
              className="px-8 py-3 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-md transition-colors duration-300 flex items-center"
            >
              Get Started <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            
            <Link 
              to="/login" 
              className="px-8 py-3 bg-transparent hover:bg-gray-800 text-white border border-gray-600 font-semibold rounded-md transition-colors duration-300"
            >
              Log In
            </Link>
          </motion.div>
        </div>
        
        <div className="absolute bottom-10 left-0 right-0 z-10 flex justify-center">
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
          >
            <a href="#features" className="text-gray-400 hover:text-white transition-colors">
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="24" 
                height="24" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              >
                <path d="M12 5v14M5 12l7 7 7-7"/>
              </svg>
            </a>
          </motion.div>
        </div>
      </section>
      
      {/* Features Section */}
      <section id="features" className="py-20 bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">How It Works</h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              JustJam makes it easy to find other musicians in your area who share your passion.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div 
              className="bg-gray-800 rounded-xl p-8 text-center"
              whileHover={{ y: -10, transition: { duration: 0.2 } }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-purple-500 bg-opacity-20 rounded-full flex items-center justify-center">
                  <Music className="w-8 h-8 text-purple-500" />
                </div>
              </div>
              <h3 className="text-xl font-bold mb-4 text-white">Create Your Profile</h3>
              <p className="text-gray-400">
                Tell us about yourself, what instrument you play, and your skill level. 
                Set your location to find others nearby.
              </p>
            </motion.div>
            
            <motion.div 
              className="bg-gray-800 rounded-xl p-8 text-center"
              whileHover={{ y: -10, transition: { duration: 0.2 } }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-purple-500 bg-opacity-20 rounded-full flex items-center justify-center">
                  <MapPin className="w-8 h-8 text-purple-500" />
                </div>
              </div>
              <h3 className="text-xl font-bold mb-4 text-white">Discover Musicians</h3>
              <p className="text-gray-400">
                Search for musicians by distance, instrument, or skill level. 
                Our map shows you exactly who's near you.
              </p>
            </motion.div>
            
            <motion.div 
              className="bg-gray-800 rounded-xl p-8 text-center"
              whileHover={{ y: -10, transition: { duration: 0.2 } }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-purple-500 bg-opacity-20 rounded-full flex items-center justify-center">
                  <Users className="w-8 h-8 text-purple-500" />
                </div>
              </div>
              <h3 className="text-xl font-bold mb-4 text-white">Connect & Jam</h3>
              <p className="text-gray-400">
                Message other musicians, schedule jam sessions, and start making music together.
                It's that simple!
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Garage Section */}
      <section className="py-20 bg-gray-950">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">Just Jam Garage</h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Garage is the companion desktop software for realtime rehearsals with the musicians
              you meet on JustJam. Set up a room, invite your bandmates, and keep your sessions
              moving between live meetups.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {[
              {
                title: 'Install the Client',
                text: 'Download the desktop app and sign in with your JustJam account.',
                icon: <Laptop className="w-7 h-7 text-purple-400" />,
              },
              {
                title: 'Create a Room',
                text: 'Start a Garage room for your band or jump into an invite from a friend.',
                icon: <Radio className="w-7 h-7 text-purple-400" />,
              },
              {
                title: 'Play Together',
                text: 'Jam in realtime and keep the energy alive between in-person sessions.',
                icon: <Headphones className="w-7 h-7 text-purple-400" />,
              },
            ].map((card, index) => (
              <motion.div
                key={card.title}
                className="bg-gray-900 border border-gray-800 rounded-xl p-8 text-center"
                whileHover={{ y: -8, transition: { duration: 0.2 } }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="flex justify-center mb-6">
                  <div className="w-14 h-14 bg-purple-500/10 rounded-full flex items-center justify-center">
                    {card.icon}
                  </div>
                </div>
                <h3 className="text-xl font-bold mb-3 text-white">{card.title}</h3>
                <p className="text-gray-400">{card.text}</p>
              </motion.div>
            ))}
          </div>

          <div className="text-center">
            <Link
              to="/register"
              className="px-8 py-3 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-md transition-colors duration-300 inline-flex items-center"
            >
              Discover Garage <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-purple-900 to-indigo-900">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">Ready to Find Your Band?</h2>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            Join thousands of musicians who are connecting and creating together every day.
          </p>
          <Link 
            to="/register" 
            className="px-8 py-3 bg-white hover:bg-gray-100 text-purple-900 font-semibold rounded-md transition-colors duration-300 inline-flex items-center"
          >
            Create Your Profile <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
